<template>
	<div class="lazy-image-loader" ref="imageContainer">
		<!-- Low-quality placeholder shown initially -->
		<v-img
			v-if="imageSrc && !imageLoaded"
			:src="imageSrc"
			:alt="alt"
			v-bind="$attrs"
			@load="onImageLoad"
			class="lazy-image"
		/>

		<!-- High-resolution image loaded when in viewport -->
		<v-img
			v-if="imageLoaded && highResSrc"
			:src="highResSrc"
			:alt="alt"
			v-bind="$attrs"
			class="lazy-image"
		/>

		<!-- Fallback if no src provided -->
		<div v-if="!imageSrc" class="image-placeholder">
			<slot name="placeholder" />
		</div>
	</div>
</template>

<script>
export default {
	name: "LazyImageLoader",
	props: {
		/**
		 * Low-quality or preview image source (shown initially)
		 */
		src: {
			type: String,
			required: true
		},
		/**
		 * High-resolution image source (loaded when in viewport)
		 */
		highResSrc: {
			type: String,
			required: true
		},
		/**
		 * Alt text for the image
		 */
		alt: {
			type: String,
			default: ""
		},
		/**
		 * Intersection Observer root margin (when to start loading)
		 * Default: 100px before image enters viewport
		 */
		rootMargin: {
			type: String,
			default: "100px"
		},
		/**
		 * Intersection Observer threshold
		 */
		threshold: {
			type: [Number, Array],
			default: 0.1
		},
		/**
		 * Root element for intersection observer
		 */
		root: {
			type: [Element, null],
			default: null
		}
	},
	data() {
		return {
			imageLoaded: false,
			imageSrc: null
		};
	},
	mounted() {
		// Use the preview image immediately
		this.imageSrc = this.src;

		// Setup intersection observer for high-res loading
		this.$nextTick(() => {
			this.setupIntersectionObserver();
		});
	},
	beforeDestroy() {
		if (this._imageObserver) {
			this._imageObserver.disconnect();
		}
	},
	methods: {
		setupIntersectionObserver() {
			const options = {
				root: this.root,
				rootMargin: this.rootMargin,
				threshold: this.threshold
			};

			this._imageObserver = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					// When image enters viewport, trigger high-res load
					if (entry.isIntersecting && !this.imageLoaded) {
						this.loadHighResImage();
						// Unobserve after loading to save resources
						this._imageObserver.unobserve(entry.target);
					}
				});
			}, options);

			// Start observing the container
			if (this.$refs.imageContainer) {
				this._imageObserver.observe(this.$refs.imageContainer);
			}
		},

		loadHighResImage() {
			// Preload the high-res image
			const img = new Image();
			img.onload = () => {
				this.imageLoaded = true;
				this.$logger.debug("high res loaded: " + this.highResSrc);
				this.$emit("loaded");
			};
			img.onerror = () => {
				this.$emit("error");
			};
			img.src = this.highResSrc;
		},

		onImageLoad() {
			this.$emit("preview-loaded");
		}
	}
};
</script>

<style scoped lang="scss">
.lazy-image-loader {
	width: 100%;
	height: 100%;
}

.lazy-image {
	width: 100%;
	height: 100%;
}

.image-placeholder {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #f5f5f5;
}
</style>
